import React, { useEffect, useState } from "react";
import { Flame, Zap, Trophy, Target, Camera, Briefcase, Star, Lock } from "lucide-react";
import { osGet, t, scoreColor } from "./osApi";
// Phase 6: CEOReport, BrandCoach, StreakCard intentionally not rendered in
// OS Growth — those are sales-flavoured and live Studio-side.

const ICONS = { flame: Flame, target: Target, camera: Camera, trophy: Trophy, star: Star, briefcase: Briefcase };

const DIM_COLORS = { overall: "#F5F0E8", lighting: "#D4A574", composition: "#7BA05B", color: "#C08FBF", storytelling: "#6FA8C9", commercial: "#E0B84C" };

const SKILL_COLORS = ["#D4A574", "#7BA05B", "#6FA8C9", "#E0B84C", "#C08FBF", "#C0604A", "#8FB58A", "#D48F74", "#74A8D4", "#B5A874"];

/* 10 director skills, each with its OWN XP bar (grows from critique scores +
   CapCut Coach lessons). No single global XP. */
function SkillGrid({ lang }) {
    const [skills, setSkills] = useState(null);
    const [milestones, setMilestones] = useState([]);
    useEffect(() => {
        (async () => {
            try {
                const r = await osGet("/os/skills");
                setSkills(r.data.skills || []);
                setMilestones(r.data.milestones || []);
            } catch { /* noop */ }
        })();
    }, []);
    if (!skills) return null;
    return (
        <section data-testid="os-skill-grid">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#F5F0E8]/40 font-bold mb-1">
                {t(lang, "Director Skill Tree", "导演技能树")}
            </p>
            <p className="text-[11px] text-[#F5F0E8]/35 mb-4">
                {t(lang, "Each skill levels up on its own — fed by your photo/video critiques and CapCut Coach.",
                    "每项技能独立升级 — 由照片/影片点评分数和 CapCut 教练自动累积。")}
            </p>
            {milestones.length > 0 && (
                <div className="mb-5 rounded-xl border border-[#E0B84C]/45 bg-[#E0B84C]/8 p-4 space-y-2" data-testid="os-skill-milestones">
                    {milestones.map((m) => (
                        <p key={m.key} className="text-[12.5px] leading-relaxed text-[#E0B84C]">
                            🎉 <span className="font-bold">{t(lang, m.label_en, m.label_cn)} → Lv.{m.level} · {t(lang, m.title_en, m.title_cn)}</span>
                            <span className="text-[#F5F0E8]/65"> — {t(lang, m.note_en, m.note_cn)}</span>
                        </p>
                    ))}
                </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5">
                {skills.map((s, i) => (
                    <div key={s.key} data-testid={`os-skill-${s.key}`}>
                        <div className="flex items-baseline justify-between text-[11.5px]">
                            <span className="text-[#F5F0E8]/70">{t(lang, s.label_en, s.label_cn)}</span>
                            <span className="text-[#F5F0E8]/45 tabular-nums">
                                <span className="font-bold" style={{ color: SKILL_COLORS[i % SKILL_COLORS.length] }}>Lv.{s.level}</span> · {s.xp} XP
                            </span>
                        </div>
                        <div className="mt-1 h-[5px] rounded-full bg-white/6 overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700"
                                style={{ width: `${s.next_pct}%`, background: SKILL_COLORS[i % SKILL_COLORS.length] }} />
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* Streak + badge wall — moved here from the CEO tab (user request: no daily
   streak pressure on the CEO homepage; gamification lives in Growth). */
function StreakCard({ lang }) {
    const [s, setS] = useState(null);
    useEffect(() => {
        (async () => { try { const r = await osGet("/os/streak"); setS(r.data); } catch { /* noop */ } })();
    }, []);
    if (!s) return null;
    const nxt = s.next_badge;
    const pct = nxt ? Math.min(100, Math.round((s.current / nxt.threshold) * 100)) : 100;
    return (
        <section className="rounded-2xl border border-[#E0B84C]/30 bg-[#E0B84C]/5 p-5" data-testid="os-streak">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#E0B84C] font-bold">{t(lang, "Challenge streak", "商业挑战连胜")}</p>
                    <p className="font-serif text-3xl mt-1" data-testid="os-streak-count">
                        🔥 {s.current} <span className="text-[13px] text-[#F5F0E8]/45">{t(lang, "days", "天")}</span>
                        <span className="ml-3 text-[12px] text-[#F5F0E8]/40">{t(lang, "best", "最佳")} {s.best}</span>
                    </p>
                </div>
                <div className="flex gap-2.5" data-testid="os-badge-wall">
                    {(s.all_badges || []).map((b) => (
                        <div key={b.threshold} title={`${t(lang, b.name_en, b.name_cn)} · ${b.threshold}${t(lang, "d", "天")}`}
                            className={`flex flex-col items-center gap-0.5 ${b.unlocked ? "" : "opacity-25 grayscale"}`}
                            data-testid={`os-badge-${b.threshold}`}>
                            <span className="text-xl">{b.icon}</span>
                            <span className="text-[8.5px] text-[#F5F0E8]/50">{b.threshold}{t(lang, "d", "天")}</span>
                        </div>
                    ))}
                </div>
            </div>
            {nxt && (
                <div className="mt-3.5">
                    <div className="flex justify-between text-[10px] text-[#F5F0E8]/45 mb-1">
                        <span>{t(lang, `Next: ${nxt.name_en}`, `下一枚：${nxt.name_cn}`)} {nxt.icon}</span>
                        <span>{s.current}/{nxt.threshold}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                        <div className="h-full rounded-full bg-[#E0B84C] transition-all duration-700" style={{ width: `${pct}%` }} />
                    </div>
                </div>
            )}
        </section>
    );
}

function WeeklyGrowth({ growth, lang }) {
    const dims = ["overall", "lighting", "composition", "color", "storytelling", "commercial"];
    const labels = { overall: t(lang, "Overall", "综合"), lighting: t(lang, "Lighting", "光线"), composition: t(lang, "Composition", "构图"), color: t(lang, "Color", "色彩"), storytelling: t(lang, "Story", "叙事"), commercial: t(lang, "Commercial", "商业") };
    const W = 640, H = 180, PAD = 28;
    const n = growth.length;
    const x = (i) => n === 1 ? W / 2 : PAD + (i * (W - 2 * PAD)) / (n - 1);
    const y = (v) => H - PAD - ((v - 40) / 60) * (H - 2 * PAD);
    return (
        <div>
            <div className="overflow-x-auto">
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[480px]">
                    {[50, 70, 90].map((g) => (
                        <g key={g}>
                            <line x1={PAD} x2={W - PAD} y1={y(g)} y2={y(g)} stroke="rgba(255,255,255,0.08)" strokeDasharray="3 5" />
                            <text x={4} y={y(g) + 3} fill="rgba(245,240,232,0.35)" fontSize="9">{g}</text>
                        </g>
                    ))}
                    {dims.map((d) => {
                        const pts = growth.filter((w) => w[d] != null);
                        if (pts.length === 0) return null;
                        const path = pts.map((w, i) => `${i === 0 ? "M" : "L"}${x(growth.indexOf(w))},${y(w[d])}`).join(" ");
                        return (
                            <g key={d}>
                                <path d={path} fill="none" stroke={DIM_COLORS[d]} strokeWidth={d === "overall" ? 2.5 : 1.5} opacity={d === "overall" ? 1 : 0.75} />
                                {pts.map((w, i) => <circle key={i} cx={x(growth.indexOf(w))} cy={y(w[d])} r={d === "overall" ? 3 : 2} fill={DIM_COLORS[d]} />)}
                            </g>
                        );
                    })}
                    {growth.map((w, i) => (
                        <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fill="rgba(245,240,232,0.45)" fontSize="9">{w.week.split("-")[1]}</text>
                    ))}
                </svg>
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
                {dims.map((d) => (
                    <span key={d} className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.12em] text-[#F5F0E8]/60">
                        <span className="h-2 w-2 rounded-full" style={{ background: DIM_COLORS[d] }} /> {labels[d]}
                    </span>
                ))}
            </div>
        </div>
    );
}

const SKILLS = [
    { k: "lighting", en: "Lighting", cn: "光线" },
    { k: "composition", en: "Composition", cn: "构图" },
    { k: "color", en: "Color", cn: "色彩" },
    { k: "storytelling", en: "Storytelling", cn: "叙事" },
    { k: "commercial", en: "Commercial", cn: "商业价值" },
];

export default function OSProgress({ lang, onProgress }) {
    const [data, setData] = useState(null);
    useEffect(() => { (async () => { try { const r = await osGet("/os/progress"); setData(r.data); } catch { /* noop */ } })(); }, []);
    if (!data) return <div className="py-20 text-center text-[#F5F0E8]/40 text-sm">…</div>;

    const p = data.progress;
    const pct = Math.min(100, Math.round((p.xp / data.next_level_xp) * 100));
    const unlockedIds = new Set((p.achievements || []).map((a) => a.id));
    const history = data.critique_history || [];

    return (
        <div className="space-y-8" data-testid="os-progress">
            {/* Phase 6 — Director Training only.
                CEO weekly report, Brand Coach daily tasks and Business
                Challenge Streak are intentionally hidden here — they live
                in Studio-side dashboards. OS Growth stays about craft
                evidence only. */}

            {/* 10-skill XP tree */}
            <SkillGrid lang={lang} />

            {/* Level + skill counters (sales-metric columns removed) */}
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-[#D4A574]/30 bg-[#D4A574]/6 p-6 text-center">
                    <p className="font-serif text-5xl text-[#D4A574]" data-testid="os-level">Lv {p.level}</p>
                    <div className="mt-3 h-2 rounded-full bg-white/8 overflow-hidden">
                        <div className="h-full bg-[#D4A574] transition-all duration-700" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[11px] text-[#F5F0E8]/50 mt-2">{p.xp} / {data.next_level_xp} XP · {t(lang, "earned from craft reviews only", "仅来自练习评析")}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/4 p-6">
                    {[
                        [t(lang, "Practice days completed", "已完成练习天数"), p.counters.days_done ?? 0],
                        [t(lang, "Works reviewed", "练习作品评析"), p.counters.critiques],
                    ].map(([k, v]) => (
                        <div key={k} className="flex justify-between text-[13px] py-1.5 border-b border-white/6 last:border-0">
                            <span className="text-[#F5F0E8]/55">{k}</span><span className="font-bold">{v}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Skills */}
            <section className="rounded-2xl border border-white/10 bg-white/4 p-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/45 font-bold mb-4">{t(lang, "Skill levels — rolling average of your last 10 critiques", "技能等级 — 最近 10 次评析的滚动平均")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                    {SKILLS.map((s) => {
                        const v = p.skills?.[s.k] || 0;
                        return (
                            <div key={s.k} className="text-center">
                                <div className="relative mx-auto h-20 w-20">
                                    <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
                                        <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
                                        <circle cx="18" cy="18" r="15.9" fill="none" stroke={scoreColor(v)} strokeWidth="3"
                                            strokeDasharray={`${v} 100`} strokeLinecap="round" />
                                    </svg>
                                    <span className="absolute inset-0 flex items-center justify-center font-serif text-xl">{v || "–"}</span>
                                </div>
                                <p className="text-[11px] uppercase tracking-[0.14em] text-[#F5F0E8]/55 mt-2">{t(lang, s.en, s.cn)}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Weekly growth — the chart Kaily asked for */}
            {(data.weekly_growth || []).length > 0 && (
                <section className="rounded-2xl border border-white/10 bg-white/4 p-6" data-testid="os-weekly-growth">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/45 font-bold mb-4">{t(lang, "Weekly growth — skill averages per week", "每周成长 — 各技能周平均分")}</p>
                    <WeeklyGrowth growth={data.weekly_growth} lang={lang} />
                </section>
            )}

            {/* Score trend */}
            {history.length > 1 && (
                <section className="rounded-2xl border border-white/10 bg-white/4 p-6">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/45 font-bold mb-4">{t(lang, "Overall score trend", "综合分趋势")}</p>
                    <div className="flex items-end gap-1.5 h-28">
                        {history.slice(-30).map((c, i) => {
                            const v = c.scores?.overall || 0;
                            return <div key={i} title={`${v}`} className="flex-1 rounded-t transition-all" style={{ height: `${v}%`, background: scoreColor(v), opacity: 0.85 }} />;
                        })}
                    </div>
                </section>
            )}

            {/* Achievements */}
            <section>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/45 font-bold mb-3">{t(lang, "Achievements", "成就")}</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {data.all_achievements.map((a) => {
                        const got = unlockedIds.has(a.id);
                        const Icon = ICONS[a.icon] || Trophy;
                        return (
                            <div key={a.id} data-testid={`os-achievement-${a.id}`}
                                className={`rounded-xl border p-4 text-center ${got ? "border-[#D4A574]/50 bg-[#D4A574]/10" : "border-white/8 bg-white/3 opacity-45"}`}>
                                {got ? <Icon size={20} className="mx-auto text-[#D4A574]" /> : <Lock size={18} className="mx-auto text-[#F5F0E8]/40" />}
                                <p className="text-[11.5px] font-bold mt-2 leading-tight">{t(lang, a.name_en, a.name_cn)}</p>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
